// src/pages/IndustryDetailPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { API_BASE } from "../config/orbisConfig";


const IndustryDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const industryId = Number(id);

    const [industry, setIndustry] = useState(null);
    const [sections, setSections] = useState([]);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const [notes, setNotes] = useState("");
    const [newSection, setNewSection] = useState({ title: "", description: "" });
    const [uploadFile, setUploadFile] = useState(null);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                setLoading(true);
                setError("");

                const [indRes, secRes, fileRes] = await Promise.all([
                    fetch(`${API_BASE}/industry_get.php?id=${industryId}`),
                    fetch(`${API_BASE}/industry_sections_list.php?industry_id=${industryId}`),
                    fetch(`${API_BASE}/industry_files_list.php?industry_id=${industryId}`),
                ]);

                const indData = await indRes.json().catch(() => null);
                const secData = await secRes.json().catch(() => []);
                const fileData = await fileRes.json().catch(() => []);

                if (!indData || !indData.id) {
                    setError("No se encontró la industria solicitada.");
                    setIndustry(null);
                } else {
                    setIndustry(indData);
                    setNotes(indData.notes || "");
                }

                setSections(Array.isArray(secData) ? secData : []);
                setFiles(Array.isArray(fileData) ? fileData : []);
            } catch (err) {
                console.error(err);
                setError("Error al cargar la industria.");
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [industryId]);

    const formatCurrency = (val) => {
        if (val == null || val === "") return "-";
        const num = Number(val);
        if (Number.isNaN(num)) return val;
        return `MXN ${num.toLocaleString()}`;
    };

    const handleSaveNotes = async () => {
        if (!industry) return;
        try {
            setSaving(true);
            setError("");

            const res = await fetch("${API_BASE}/industry_update.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    id: industryId,
                    notes,
                }),
            });

            const data = await res.json().catch(() => ({}));
            if (!res.ok || data.success === false) {
                throw new Error(data.error || "No se pudieron actualizar las notas.");
            }

            setIndustry((prev) => ({ ...prev, notes }));
            alert("Notas actualizadas.");
        } catch (err) {
            console.error(err);
            setError(err.message || "Error al guardar notas.");
        } finally {
            setSaving(false);
        }
    };

    const handleAddSection = async (e) => {
        e.preventDefault();
        if (!newSection.title.trim()) return;
        try {
            setSaving(true);
            setError("");

            const res = await fetch("${API_BASE}/industry_section_create.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    industry_id: industryId,
                    title: newSection.title,
                    description: newSection.description,
                }),
            });

            const data = await res.json().catch(() => ({}));
            if (!res.ok || data.success === false) {
                throw new Error(data.error || "No se pudo crear la sección.");
            }

            setSections((prev) => [...prev, data.section || {
                id: data.id,
                industry_id: industryId,
                title: newSection.title,
                description: newSection.description,
            }]);

            setNewSection({ title: "", description: "" });
        } catch (err) {
            console.error(err);
            setError(err.message || "Error al agregar sección.");
        } finally {
            setSaving(false);
        }
    };

    const handleUploadFile = async (e) => {
        e.preventDefault();
        if (!uploadFile) return;

        try {
            setSaving(true);
            setError("");

            const formData = new FormData();
            formData.append("industry_id", industryId);
            formData.append("file", uploadFile);

            const res = await fetch("${API_BASE}/industry_file_upload.php", {
                method: "POST",
                body: formData,
            });

            const data = await res.json().catch(() => ({}));
            if (!res.ok || data.success === false) {
                throw new Error(data.error || "No se pudo subir el archivo.");
            }

            if (data.file) {
                setFiles((prev) => [...prev, data.file]);
            }

            setUploadFile(null);
            e.target.reset();
        } catch (err) {
            console.error(err);
            setError(err.message || "Error al subir archivo.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="p-4 text-xs text-gray-500">Cargando industria…</div>
            </MainLayout>
        );
    }

    if (!industry) {
        return (
            <MainLayout>
                <div className="p-4">
                    {error && (
                        <div className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}
                    <button
                        onClick={() => navigate("/industries")}
                        className="px-3 py-1.5 rounded-lg border border-cani-border text-xs text-gray-700 hover:bg-cani-bg"
                    >
                        ← Volver a industrias
                    </button>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="p-4 space-y-4">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <button
                            type="button"
                            onClick={() => navigate("/industries")}
                            className="text-[11px] text-cani-primary hover:underline mb-1"
                        >
                            ← Volver a industrias
                        </button>
                        <h2 className="text-lg md:text-xl font-semibold text-cani-dark">
                            {industry.name}
                        </h2>
                        <p className="text-xs md:text-sm text-gray-500">
                            Eje estratégico: {industry.code} · {industry.concept || ""}
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="mb-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        {error}
                    </div>
                )}

                {/* Datos principales + notas */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white border border-cani-border rounded-xl shadow-sm p-4 text-xs space-y-2">
                        <h3 className="text-[12px] font-semibold text-cani-dark mb-1">
                            Información del eje / industria
                        </h3>
                        <p>
                            <span className="font-semibold text-gray-600">Nombre: </span>
                            {industry.name}
                        </p>
                        <p>
                            <span className="font-semibold text-gray-600">Código: </span>
                            {industry.code}
                        </p>
                        {industry.concept && (
                            <p>
                                <span className="font-semibold text-gray-600">
                                    Concepto:{" "}
                                </span>
                                {industry.concept}
                            </p>
                        )}
                        {industry.description && (
                            <p>
                                <span className="font-semibold text-gray-600">
                                    Descripción:{" "}
                                </span>
                                {industry.description}
                            </p>
                        )}
                        <p>
                            <span className="font-semibold text-gray-600">
                                PIB (referencia):{" "}
                            </span>
                            {formatCurrency(industry.pib)}
                        </p>
                    </div>

                    {/* Notas estratégicas */}
                    <div className="bg-white border border-cani-border rounded-xl shadow-sm p-4 text-xs space-y-2">
                        <h3 className="text-[12px] font-semibold text-cani-dark mb-1">
                            Notas estratégicas de este eje
                        </h3>
                        <textarea
                            rows={6}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="w-full border border-cani-border rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-cani-primary resize-none"
                            placeholder="Escribe aquí tus notas estratégicas, retos, oportunidades y vínculos con proyectos CANIETI."
                        />
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={handleSaveNotes}
                                disabled={saving}
                                className="px-3 py-1.5 rounded-lg bg-cani-primary text-white text-xs hover:bg-cani-primaryLight disabled:opacity-60"
                            >
                                {saving ? "Guardando..." : "Guardar notas"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Secciones y documentos */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Secciones */}
                    <div className="bg-white border border-cani-border rounded-xl shadow-sm p-4 text-xs space-y-3">
                        <h3 className="text-[12px] font-semibold text-cani-dark mb-1">
                            Secciones del eje (talento, infraestructura, cluster, etc.)
                        </h3>

                        <ul className="space-y-2 max-h-60 overflow-y-auto">
                            {sections.length === 0 && (
                                <li className="text-[11px] text-gray-400">
                                    Aún no hay secciones registradas.
                                </li>
                            )}
                            {sections.map((s) => (
                                <li
                                    key={s.id}
                                    className="border border-cani-border rounded-lg px-3 py-2 hover:bg-cani-bg/60"
                                >
                                    <div className="text-[11px] font-semibold text-cani-dark">
                                        {s.title}
                                    </div>
                                    {s.description && (
                                        <div className="text-[11px] text-gray-600">
                                            {s.description}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>

                        <form onSubmit={handleAddSection} className="space-y-2 pt-2">
                            <h4 className="text-[11px] font-semibold text-gray-700">
                                Agregar sección
                            </h4>
                            <input
                                type="text"
                                placeholder="Título de la sección"
                                value={newSection.title}
                                onChange={(e) =>
                                    setNewSection((prev) => ({
                                        ...prev,
                                        title: e.target.value,
                                    }))
                                }
                                className="w-full border border-cani-border rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-cani-primary"
                            />
                            <textarea
                                rows={3}
                                placeholder="Descripción breve (opcional)"
                                value={newSection.description}
                                onChange={(e) =>
                                    setNewSection((prev) => ({
                                        ...prev,
                                        description: e.target.value,
                                    }))
                                }
                                className="w-full border border-cani-border rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-cani-primary resize-none"
                            />
                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-3 py-1.5 rounded-lg border border-cani-border text-xs text-gray-700 hover:bg-cani-bg disabled:opacity-60"
                                >
                                    {saving ? "Guardando..." : "Agregar sección"}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Documentos */}
                    <div className="bg-white border border-cani-border rounded-xl shadow-sm p-4 text-xs space-y-3">
                        <h3 className="text-[12px] font-semibold text-cani-dark mb-1">
                            Documentos asociados a este eje
                        </h3>

                        <ul className="space-y-2 max-h-60 overflow-y-auto">
                            {files.length === 0 && (
                                <li className="text-[11px] text-gray-400">
                                    Aún no hay documentos cargados.
                                </li>
                            )}
                            {files.map((f) => (
                                <li
                                    key={f.id}
                                    className="border border-cani-border rounded-lg px-3 py-2 hover:bg-cani-bg/60 flex justify-between items-center gap-2"
                                >
                                    <div className="text-[11px] text-cani-dark">
                                        {f.original_name || f.file_name}
                                    </div>
                                    <a
                                        href={f.file_path}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[11px] text-cani-primary hover:underline"
                                    >
                                        Ver
                                    </a>
                                </li>
                            ))}
                        </ul>

                        <form
                            onSubmit={handleUploadFile}
                            className="space-y-2 pt-2"
                            encType="multipart/form-data"
                        >
                            <h4 className="text-[11px] font-semibold text-gray-700">
                                Subir documento
                            </h4>
                            <input
                                type="file"
                                onChange={(e) => setUploadFile(e.target.files[0] || null)}
                                className="w-full text-[11px]"
                            />
                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-3 py-1.5 rounded-lg border border-cani-border text-xs text-gray-700 hover:bg-cani-bg disabled:opacity-60"
                                >
                                    {saving ? "Subiendo..." : "Subir archivo"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default IndustryDetailPage;
